import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle } from "lucide-react";
import { parseBooleanFlag } from "@/lib/env";

const STORAGE_KEY = "maintenance_notification_dismissed";

interface MaintenanceConfig {
  enabled: boolean;
  title: string;
  message: string;
  scheduledDate?: string;
}

const DEFAULT_CONFIG: MaintenanceConfig = {
  enabled: parseBooleanFlag(import.meta.env.VITE_MAINTENANCE_NOTIFICATION) ?? false,
  title: "メンテナンスのお知らせ",
  message:
    "システムの改善作業を行うため、一時的にサービスが利用できなくなる場合があります。ご不便をおかけしますが、ご理解のほどよろしくお願いいたします。",
};

function getTodayKey(): string {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
}

function isDismissedToday(): boolean {
  try {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) return false;
    return dismissed === getTodayKey();
  } catch {
    return false;
  }
}

function dismissForToday(): void {
  try {
    localStorage.setItem(STORAGE_KEY, getTodayKey());
  } catch {
    // localStorage unavailable
  }
}

export function MaintenanceNotification() {
  const [open, setOpen] = useState(false);
  const [dontShowToday, setDontShowToday] = useState(false);
  const [config] = useState<MaintenanceConfig>(DEFAULT_CONFIG);

  useEffect(() => {
    if (config.enabled && !isDismissedToday()) {
      const timer = setTimeout(() => setOpen(true), 500);
      return () => clearTimeout(timer);
    }
  }, [config.enabled]);

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen && dontShowToday) {
      dismissForToday();
    }
    setOpen(nextOpen);
  };

  if (!config.enabled) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <DialogTitle>{config.title}</DialogTitle>
          </div>
          <DialogDescription className="pt-2 text-left">
            {config.message}
          </DialogDescription>
          {config.scheduledDate && (
            <p className="text-sm font-medium text-foreground pt-2">
              予定日時: {config.scheduledDate}
            </p>
          )}
        </DialogHeader>
        <DialogFooter className="flex-col gap-3 sm:flex-col">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="dont-show-today"
              checked={dontShowToday}
              onCheckedChange={(checked) => setDontShowToday(checked === true)}
            />
            <label
              htmlFor="dont-show-today"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              今日は表示しない
            </label>
          </div>
          <Button onClick={() => handleClose(false)} className="w-full">
            閉じる
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
